/**
 * Cteamscarousel Component
 * Native-scroll horizontal carousel with CSS scroll-snap, an infinite-feeling
 * loop-reset, and motion-value-driven distance-from-center visuals.
 *
 * Architecture:
 *   - The container is a real horizontally-scrolling element. Wheel,
 *     trackpad, and touch input go straight through the browser — no custom
 *     pointer or wheel handlers. `overscroll-behavior-x: contain` blocks the
 *     Mac back-nav gesture at the edges.
 *   - The team list is rendered TRIPLED. The user starts in the middle copy;
 *     after each scroll settles, if they've crossed into an outer copy we
 *     instantly snap `scrollLeft` to the equivalent position in the middle
 *     copy. The cards at both ends are pixel-identical, so the reset is
 *     invisible.
 *   - `scrollX` mirrors `container.scrollLeft` into a MotionValue (one
 *     passive scroll listener, no React renders for the mirror itself).
 *   - Each card derives `distance = |scrollX - homeScroll|` via
 *     `useTransform`, and pipes that into scale/opacity on a `motion.div`.
 *     Framer-motion writes the transforms directly to the DOM.
 *   - "Settled" is detected by debouncing the scroll event 200ms; that's
 *     when `onCenteredTeamChange` hydrates the parent with the active team.
 */
"use client";
import { forwardRef } from "react";
import { useViewport } from "@/hooks/dom";
import { useViewportMetrics } from "@/hooks/dom";
import { useModels } from "@/contexts/models";
import { Viewport } from "@/theme/breakpoints";
import { CarouselCardStrip } from "./CarouselCardStrip";
import type { CteamscarouselProps, CteamscarouselRef } from "./types";
import { useCarouselGeometry } from "./useCarouselGeometry";
import { useCarouselLayoutStyles } from "./useCarouselLayoutStyles";
import { useCarouselScrollState } from "./useCarouselScrollState";
import { useCarouselTelemetry } from "./useCarouselTelemetry";
import { useCarouselVendors } from "./useCarouselVendors";
import { useTripledCarouselItems } from "./useTripledCarouselItems";
export const Cteamscarousel = forwardRef<CteamscarouselRef, CteamscarouselProps>(function Cteamscarousel({ teams, className, style, onTeamSelect, onCenteredTeamChange, cardContainerPreset, entranceReady = false, }: CteamscarouselProps, ref) {
    const { viewport } = useViewport();
    const { vw } = useViewportMetrics();
    const isMobile = viewport === Viewport.Mobile;
    const { getUniqueVendors } = useModels();
    const { initialDoubledIndex, items, itemsLength, middleCopyEnd, middleCopyStart, originalLength, } = useTripledCarouselItems(teams);
    const { cardGap, cardHeight, cardWidth, pitch } = useCarouselGeometry({
        isMobile,
        vw,
    });
    const vendorsByTeamId = useCarouselVendors({ teams, getUniqueVendors });
    const { centeredDoubledIndex, containerRef, handleScrollToIndex, handleSelectTeam, scrollX, } = useCarouselScrollState({
        initialDoubledIndex,
        middleCopyEnd,
        middleCopyStart,
        onCenteredTeamChange,
        onTeamSelect,
        originalLength,
        pitch,
        ref,
        teams,
    });
    useCarouselTelemetry({
        centeredDoubledIndex,
        containerRef,
        geometry: {
            cardGap,
            cardHeight,
            cardWidth,
            isMobile,
            pitch,
        },
        itemsLength,
        originalLength,
    });
    const { rootStyle, scrollerStyle, spacerStyle } = useCarouselLayoutStyles({
        cardWidth,
        isMobile,
        style,
        vw,
    });
    return <div data-el="Cteamscarousel/Root" style={rootStyle} className={className}>
            <div ref={containerRef} className="cteamscarousel-scroll-container" style={scrollerStyle}>
                <CarouselCardStrip cardContainerPreset={cardContainerPreset} cardGap={cardGap} cardHeight={cardHeight} cardWidth={cardWidth} centeredDoubledIndex={centeredDoubledIndex} entranceReady={entranceReady} handleScrollToIndex={handleScrollToIndex} handleSelectTeam={handleSelectTeam} isMobile={isMobile} items={items} pitch={pitch} scrollX={scrollX} spacerStyle={spacerStyle} vendorsByTeamId={vendorsByTeamId}/>
            </div>
        </div>
        ;
});
export type { CteamscarouselProps, CteamscarouselRef } from "./types";
